import React, { FC } from 'react'
import { Heading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import {
  Contract,
  ContractTenant,
  UserMetadata,
} from '../../../../common/validations'
import { ModificationFormLayout } from '../../ContractChanges/ModificationForm/ModificationFormLayout/ModificationFormLayout'
import { ModificationFormButtons } from '../../ContractChanges/ModificationForm/ModificationFormButtons/ModificationFormButtons'
import { TenantSelectionNotification } from '../../ContractChanges/ModificationForm/TenantSelectionNotification/TenantSelectionNotification'
import { ContractPartyMoveOutDetails } from './ContractPartyMoveOutDetails/ContractPartyMoveOutDetails'
import { TenantChoice } from '../../ContractChanges/components/TenantChoice/TenantChoice'
import { useModifyContractForm } from '../../ContractChanges/hooks/useModifyContractForm'
import { ContractChangesViewState } from '../../ContractChanges/types'
import { useMonthEndMoveOutDate } from './hooks/useMonthEndMoveOutDate'
import { useContractPartyChoice } from './hooks/useContractPartyChoice'

type Props = {
  view: ContractChangesViewState
  setView: (view: ContractChangesViewState) => void
  contract: Contract
  tenants: ContractTenant[]
  userMetadata: UserMetadata
  onCancel: () => void
}

export const ContractTransferForm: FC<Props> = ({
  view,
  setView,
  contract,
  tenants,
  userMetadata,
  onCancel,
}) => {
  const { t } = useTranslation(['contractModification'])
  const {
    now,
    inEffectDisabledBefore,
    inEffectDisabledAfter,
    movingOutTenantIds,
    contractParties,
    initialValues,
    getPossibleNewContractParties,
    changeMovingOutTenants,
    showPreview,
    cancel,
  } = useModifyContractForm({ contract, tenants, setView, onCancel })
  const { allowedDates, description, validate } = useMonthEndMoveOutDate(
    now,
    inEffectDisabledAfter
  )
  const { resolveContractPartyChoice } = useContractPartyChoice({
    contract,
    tenants,
    movingOutTenantIds,
    changeMovingOutTenants,
  })

  const otherContractPartySelected = contractParties.some(
    (contractParty) =>
      contractParty.id !== contract.tenant.id &&
      movingOutTenantIds.includes(contractParty.id)
  )
  const showTransferTenantField = contractParties.every((contractParty) =>
    movingOutTenantIds.includes(contractParty.id)
  )

  const possibleNewContractPartiesOptions = getPossibleNewContractParties(
    movingOutTenantIds
  ).map((party) => ({
    id: party.id,
    label: `${party.firstName} ${party.lastName}`,
  }))

  return (
    <ModificationFormLayout
      view={view}
      setView={setView}
      contract={contract}
      tenants={tenants}
      userMetadata={userMetadata}
      initialValues={initialValues}
      validate={validate}
    >
      {(form, genericError) => (
        <>
          <Heading element="h3" size="l" className="mb-6 font-medium">
            {t('contractModification:headings.contractPartiesMovingOut')}
          </Heading>

          {contractParties.map((tenant) => (
            <TenantChoice
              key={tenant.id}
              tenant={tenant}
              isCurrentUser={tenant.id === contract.tenant.id}
              choice={resolveContractPartyChoice(form, tenant)}
            />
          ))}

          {movingOutTenantIds.length > 0 && (
            <>
              <div className="mt-6">
                <TenantSelectionNotification
                  otherContractPartySelected={otherContractPartySelected}
                  genericError={genericError}
                />
              </div>

              {!otherContractPartySelected && (
                <ContractPartyMoveOutDetails
                  form={form}
                  movingOutCount={movingOutTenantIds.length}
                  transferDestination={
                    showTransferTenantField
                      ? {
                          options: possibleNewContractPartiesOptions,
                          sourceTenantId: contractParties[0].id,
                        }
                      : undefined
                  }
                  inEffectField={{
                    description,
                    allowedDates,
                    disabledBefore: inEffectDisabledBefore,
                    disabledAfter: inEffectDisabledAfter,
                  }}
                />
              )}

              <ModificationFormButtons
                onCancel={() => cancel(form)}
                onPreview={() => showPreview(form)}
                previewDisabled={
                  form.isSubmitting ||
                  otherContractPartySelected ||
                  Boolean(genericError)
                }
              />
            </>
          )}
        </>
      )}
    </ModificationFormLayout>
  )
}
